import { useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { config, STEP_CREDIT_LIMIT_REQUEST, STEP_BALANCE_TRANSFER } from '_config';
import { useStoreValue } from 'store';
import { isFeatureOn } from 'featureToggles';
import { defaultBTFormValues } from '_config/defaultFormValues';
import { useErrorTracking } from './useErrorTrackingAndUpdateStore';
import { useHandleApiErrors } from './useHandleApiErrors';

export const SAVE_DRAFT_GQL = gql`
  mutation saveDraft($input: SaveDraftInput!) {
    saveDraft(input: $input) {
      applicationId
    }
  }
`;

export function useSaveDraft({ onSuccess, onError } = {}) {
  const { handleErrorTracking } = useErrorTracking('hook:useSaveDraft');
  const { handleError } = useHandleApiErrors('hook:useSaveDraft', onError);
  const [store] = useStoreValue();
  const [commitMutation, { loading }] = useMutation(SAVE_DRAFT_GQL, {
    onCompleted: onSuccess
      ? data => {
          onSuccess(data.saveDraft);
        }
      : undefined,
    onError: err => {
      const { oktaAuthTransaction, ...draftStoreState } = store;
      // eslint-disable-next-line no-console
      console.error(`saveDraft API error: ${JSON.stringify(err)},
        applicationId: ${store.applicationId},
        productCode: ${config.productCode},
        hasDraft: ${!!draftStoreState}`);
      handleErrorTracking(err);
      if (onError) {
        handleError(err);
      }
    },
  });

  const sanitizeStoreState = useCallback(storeState => {
    let { activeStep } = storeState;
    if (activeStep === STEP_CREDIT_LIMIT_REQUEST) {
      // if the user is on the credit limit request step, we want to save the draft as if they are on the balance transfer step
      activeStep = STEP_BALANCE_TRANSFER;
    }
    return {
      ...storeState,
      activeStep,
      acquisition: {
        ...storeState.acquisition,
        ...defaultBTFormValues,
      },
    };
  }, []);

  const saveDraft = useCallback(
    storeState => {
      if (!isFeatureOn('saveAndResume')) {
        return;
      }
      const { oktaAuthTransaction, ...draftStoreState } = storeState;
      commitMutation({
        variables: {
          input: {
            applicationID: storeState.applicationId,
            productCode: config.productCode,
            draft: JSON.stringify(sanitizeStoreState(draftStoreState)),
          },
        },
      });
    },
    [commitMutation, sanitizeStoreState],
  );

  return {
    saveDraft,
    savingDraft: loading,
  };
}
